import { useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useAuthState } from '../../app/session/authState';
import { ConfirmDialog } from '../../chrome/form';
import { Page, PageHeader, PageSection } from '../../chrome/page';
import { opsTitles } from '../shell/titles';
import { EmptyState, ErrorState, LoadingState } from '../../chrome/state';
import { getLocale } from '../../i18n';
import { integer } from '../../i18n/format';
import type { ApiFailure } from '../../lib/http/errors';
import { ApiFailureError } from '../../lib/http/errors';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { opsKeys, useOpsAdmins, useRevokeAdmin } from '../api/queries';
import type { PlatformAdmin } from '../api/schemas';
import { formatDate } from '../lib/date';
import { GrantAdminDialog } from './GrantAdminDialog';
import { adminsCatalogue, t } from './messages';

function AdminTooltip(props: { content: ReactNode; children: ReactNode }): ReactNode {
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex --
              stands in for the disabled button inside, which cannot itself
              take focus, so a keyboard user can still reach the tooltip. */}
          <span tabIndex={0} className="inline-block">
            {props.children}
          </span>
        </TooltipTrigger>
        <TooltipContent>{props.content}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function Admins(): ReactNode {
  const query = useOpsAdmins();
  const revokeAdmin = useRevokeAdmin();
  const queryClient = useQueryClient();
  const locale = getLocale();
  const session = useAuthState();
  const viewerEmail = session.status === 'signed-in' ? (session.user?.email ?? null) : null;

  const [revokingAdmin, setRevokingAdmin] = useState<PlatformAdmin | null>(null);
  const [revokeFailure, setRevokeFailure] = useState<ApiFailure | null>(null);

  async function handleRevoke(): Promise<void> {
    if (!revokingAdmin) return;
    setRevokeFailure(null);
    try {
      await revokeAdmin.mutateAsync({ userId: revokingAdmin.user_id });
      setRevokingAdmin(null);
      await queryClient.invalidateQueries({ queryKey: opsKeys.admins() });
    } catch (cause) {
      if (cause instanceof ApiFailureError && cause.failure.kind === 'conflict') {
        setRevokeFailure({
          kind: 'invalid',
          field: null,
          message: t('admins.lastAdmin.refused'),
        });
      } else if (cause instanceof ApiFailureError) {
        setRevokeFailure(cause.failure);
      } else {
        setRevokeFailure({ kind: 'server', status: 500 });
      }
      await query.refetch();
    }
  }

  if (query.isPending) {
    return (
      <Page>
        <PageHeader
          title={opsTitles.t('admins')}
          actions={<GrantAdminDialog trigger={<Button>{t('admins.grant.open')}</Button>} />}
        />
        <LoadingState />
      </Page>
    );
  }

  if (query.isError) {
    return (
      <Page>
        <PageHeader
          title={opsTitles.t('admins')}
          actions={<GrantAdminDialog trigger={<Button>{t('admins.grant.open')}</Button>} />}
        />
        <ErrorState
          failure={query.error?.failure ?? { kind: 'server', status: 500 }}
          onRetry={() => void query.refetch()}
        />
      </Page>
    );
  }

  const admins = query.data;
  const isOnlyAdmin = admins.length === 1;

  return (
    <Page>
      <PageHeader
        title={opsTitles.t('admins')}
        actions={<GrantAdminDialog trigger={<Button>{t('admins.grant.open')}</Button>} />}
      />

      <PageSection
        title={t('admins.section.title')}
        actions={
          <Badge variant="secondary">
            {adminsCatalogue.plural('admins.count.other', admins.length, {
              n: integer(admins.length, locale),
            })}
          </Badge>
        }
      >
        {admins.length === 0 ? (
          <EmptyState title={t('admins.empty.title')} description={t('admins.empty.body')} />
        ) : (
          <div className="rounded-lg border">
            <Table label={t('admins.section.title')}>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('admins.table.person')}</TableHead>
                  <TableHead className="hidden sm:table-cell">
                    {t('admins.table.grantedAt')}
                  </TableHead>
                  <TableHead className="text-end">
                    <span className="sr-only">{t('admins.table.actions')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.user_id} data-testid={`admin-${admin.user_id}`}>
                    <TableCell>
                      <div className="flex flex-col">
                        {/* No display name on the wire yet (`PlatformAdminOut` carries only
                            `email`): the address is the whole line rather than pairing a name
                            over a raw id nobody here can resolve to one. */}
                        <span className="flex items-center gap-2 font-medium text-foreground">
                          {admin.email ?? (
                            <span className="text-muted-foreground font-normal italic">
                              {t('admins.unverified')}
                            </span>
                          )}
                          {admin.email !== null && admin.email === viewerEmail && (
                            <Badge variant="secondary" data-testid="you-badge">
                              {t('admins.you')}
                            </Badge>
                          )}
                        </span>
                        {/* The date column is dropped below `sm`, where it and the
                            action would not both fit; the date moves under the person. */}
                        <time
                          dateTime={admin.granted_at}
                          className="text-caption text-muted-foreground sm:hidden"
                        >
                          {formatDate(admin.granted_at, locale)}
                        </time>
                      </div>
                    </TableCell>
                    <TableCell className="hidden sm:table-cell">
                      <time dateTime={admin.granted_at}>
                        {formatDate(admin.granted_at, locale)}
                      </time>
                    </TableCell>
                    <TableCell className="text-end">
                      {isOnlyAdmin ? (
                        <AdminTooltip content={t('admins.lastAdmin.disabledReason')}>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled
                            aria-label={t('admins.revoke.action')}
                          >
                            {t('admins.revoke.action')}
                          </Button>
                        </AdminTooltip>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setRevokeFailure(null);
                            setRevokingAdmin(admin);
                          }}
                          aria-label={t('admins.revoke.action')}
                        >
                          {t('admins.revoke.action')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </PageSection>

      <ConfirmDialog
        open={revokingAdmin !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRevokingAdmin(null);
            setRevokeFailure(null);
          }
        }}
        destructive
        title={t('admins.revoke.confirm.title')}
        description={t('admins.revoke.confirm.body')}
        confirmLabel={t('admins.revoke.confirm.confirm')}
        cancelLabel={t('admins.revoke.confirm.cancel')}
        pending={revokeAdmin.isPending}
        failure={revokeFailure}
        onConfirm={() => void handleRevoke()}
      />
    </Page>
  );
}
